import { Currency, Money } from "@hubrise/react-components"

import {
  CategoryId,
  ChargeId,
  DealId,
  DealLineId,
  DealLineSkuId,
  DiscountId,
  OptionId,
  OptionListId,
  ProductId,
  SkuId,
  VariantId,
} from "./ids"
import { TaxCombination } from "./utils/tax"

export const weightUnits = ["mg", "g", "kg"] as const
export type WeightUnit = (typeof weightUnits)[number]
export type Weight = `${number} ${WeightUnit}`

export const nutrientWeightUnits = ["ug", "mg", "g"] as const
export type NutrientWeightUnit = (typeof nutrientWeightUnits)[number]
export type NutrientWeight = `${number} ${NutrientWeightUnit}`

export const volumeUnits = ["ml", "cl", "l"] as const
export type VolumeUnit = (typeof volumeUnits)[number]
export type Volume = `${number} ${VolumeUnit}`

export type Dimension = Weight | Volume | NutrientWeight
export type DimensionType<D extends Dimension> = D extends Weight
  ? "weight"
  : D extends Volume
    ? "volume"
    : "nutrient_weight"
export type DimensionUnit<D extends Dimension> = D extends Weight
  ? WeightUnit
  : D extends Volume
    ? VolumeUnit
    : NutrientWeightUnit

export interface ICatalogSummary {
  id: string
  name: string
}

export interface IAccount {
  id: string
  name: string
  currency: Currency
}

export type IdGenerator = () => number

export interface ICatalog {
  idGenerator: IdGenerator
  hrId: string
  name: string
  currency: Currency
  data: {
    variants: Array<IVariant>
    categories: Array<ICategory>
    products: Array<IProduct>
    optionLists: Array<IOptionList>
    deals: Array<IDeal>
    discounts: Array<IDiscount>
    charges: Array<ICharge>
  }
}

export interface IVariant {
  id: VariantId
  ref: string
  name: string
}

export interface IRestrictions {
  enabled: boolean
  variantRefs?: Array<string>
  dow?: string
  startTime?: string
  endTime?: string
  startDate?: string
  endDate?: string
  serviceTypes?: Array<string>
  serviceTypeRefs?: Array<string>
  minOrderAmount?: string
  maxPerOrder?: number
  maxPerCustomer?: number
}

export interface IPriceOverride {
  variantRefs?: Array<string>
  dow?: string
  startTime?: string
  endTime?: string
  startDate?: string
  endDate?: string
  serviceTypes?: Array<string>
  serviceTypeRefs?: Array<string>
  price: Money
}

export interface ICategory {
  id: CategoryId
  parentId: CategoryId | null
  name: string
  description: string
  tags: Array<string>
  imageHrIds: Array<string>
}

export interface IProduct {
  id: ProductId
  categoryId: CategoryId
  name: string
  description: string
  tags: Array<string>
  legacyAllergenTags: Array<LegacyAllergenTag> // Store separately to pass them through in import/export cycles
  nutrition: INutrition | null
  taxRate: TaxCombination | null
  imageHrIds: Array<string>
  skus: Array<ISku>
}

export interface INutrition {
  legal_name: string | null
  ingredients: string[] | null
  additives: Additive[] | null
  allergens: Allergen[] | null
  per_100_values: IPer100Values | null
  alcohol_percentage: number | null
  caffeine_mg_per_100ml: number | null
  producer_name: string | null
  producer_brand: string | null
  producer_address: string | null
  country_of_origin: string | null
}

export interface IPer100Values {
  per: Per100Per
  energy: IPer100Energy | null
  fat: NutrientWeight | null
  saturated_fat: NutrientWeight | null
  mono_unsaturated_fat: NutrientWeight | null
  poly_unsaturated_fat: NutrientWeight | null
  carbohydrate: NutrientWeight | null
  sugar: NutrientWeight | null
  polyols: NutrientWeight | null
  starch: NutrientWeight | null
  fibre: NutrientWeight | null
  protein: NutrientWeight | null
  salt: NutrientWeight | null
}

export interface IPer100Energy {
  unit: EnergyUnit
  lower: number
  upper: number | null
}

export type EnergyUnit = "kcal" | "kJ"
export type Per100Per = "100 g" | "100 ml"

// prettier-ignore
export const LEGACY_ALLERGEN_TAGS = [
  "allergen_celery", "allergen_crustaceans", "allergen_eggs", "allergen_fish", "allergen_gluten", "allergen_lupin",
  "allergen_milk", "allergen_molluscs", "allergen_mustard", "allergen_nuts", "allergen_peanuts",
  "allergen_sesame_seeds", "allergen_soybeans", "allergen_sulphur_dioxide_sulphites", "no_allergens",
] as const

export type LegacyAllergenTag = (typeof LEGACY_ALLERGEN_TAGS)[number]

// prettier-ignore
export const ADDITIVES = [
  "acidifier", "acidity_regulator", "anti_caking_agent", "anti_foaming_agent",
  "antioxidant", "bulking_agent", "carrier", "colour_adverse_effect", "colour_other",
  "emulsifier", "emulsifying_salt", "firming_agent", "flavour_enhancer",
  "flour_treatment_agent", "foaming_agent", "gelling_agent", "glazing_agent",
  "humectant", "modified_starch", "packaging_gas", "phosphate",
  "preservative_nitrate", "preservative_nitrite_curing_salt", "preservative_other",
  "preservative_sulphites", "propellant", "raising_agent", "sequestrant",
  "stabiliser", "sweetener_contains_phenylalanine", "sweetener_laxative_effect",
  "sweetener_other", "thickener",
] as const
export type Additive = (typeof ADDITIVES)[number]

// prettier-ignore
export const ALLERGENS = [
  "celery", "crustaceans", "eggs", "fish",
  "gluten_barley", "gluten_khorasan", "gluten_oats", "gluten_rye", "gluten_spelt", "gluten_wheat",
  "lupin", "milk", "molluscs", "mustard",
  "nuts_almond", "nuts_brazil", "nuts_cashew", "nuts_hazelnut",
  "nuts_macadamia_or_queensland", "nuts_pecan", "nuts_pistachio", "nuts_walnut",
  "peanuts", "sesame_seeds", "soybeans", "sulphur_dioxide_sulphites",
] as const
export type Allergen = (typeof ALLERGENS)[number]

export interface ISku {
  id: SkuId
  ref: string
  name: string
  restrictions: IRestrictions
  priceOverrides: Array<IPriceOverride>
  price: Money
  optionListIds: Array<OptionListId>
  tags: Array<string>
  barcodes: Array<string>
  weight: Weight | null
  volume: Volume | null
  nbServings: number | null
}

export type ICategoryOrProduct = ICategory | IProduct
export type IProductOrDeal = IProduct | IDeal

type IModel = ICategory | IProduct | ISku | IOptionList | IOption | IDeal | ICharge | IDiscount
export const isCategory = (item: IModel): item is ICategory => (item as ICategory).parentId !== undefined
export const isProduct = (item: IModel): item is IProduct => (item as IProduct).skus !== undefined
export const isSku = (item: IModel): item is ISku => (item as ISku).optionListIds !== undefined
export const isDeal = (item: IModel): item is IDeal => (item as IDeal).lines !== undefined

export interface IOptionList {
  id: OptionListId
  name: string
  min_selections: number
  max_selections: number | null
  multiple_selection: boolean
  tags: Array<string>
  options: Array<IOption>
}

export interface IOption {
  id: OptionId
  ref: string
  name: string
  restrictions: IRestrictions
  priceOverrides: Array<IPriceOverride>
  price: Money
  isDefault: boolean
  tags: Array<string>
  nutrition: INutrition | null
  weight: Weight | null
  volume: Volume | null
}

export interface IDeal {
  id: DealId
  ref: string
  categoryId: CategoryId | null
  name: string
  description: string
  restrictions: IRestrictions
  couponCodes: Array<string>
  tags: Array<string>
  imageHrIds: Array<string>
  lines: Array<IDealLine>
}

export type DealLinePricing =
  | {
      pricingEffect: "unchanged"
      pricingValue: null
    }
  | {
      pricingEffect: "fixed_price" | "price_off"
      pricingValue: Money
    }
  | {
      pricingEffect: "percentage_off"
      pricingValue: number
    }

export type IDealLine = {
  id: DealLineId
  label: string
  skus: Array<IDealLineSku>
} & DealLinePricing

export type IDealLineSku = {
  id: DealLineSkuId
  skuId: SkuId
  extraCharge: Money | null
}

export type DiscountPricing =
  | {
      pricingEffect: "price_off"
      pricingValue: Money
    }
  | {
      pricingEffect: "percentage_off"
      pricingValue: number
    }

export type IDiscount = {
  id: DiscountId
  ref: string
  name: string
  description: string
  restrictions: IRestrictions
  couponCodes: Array<string>
  imageHrIds: Array<string>
} & DiscountPricing

export interface ICharge {
  id: ChargeId
  ref: string
  name: string
  type: "delivery" | "payment_fee" | "tip" | "tax" | "other"
  restrictions: IRestrictions
  price: Money | null
}
